import { useState } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    AreaChart,
    Area,
    ComposedChart,
    Scatter,
    PieChart,
    Pie,
    Cell,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
} from 'recharts';
import { motion } from 'framer-motion';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { CalendarIcon, ShoppingCart, TrendingUp, Banknote, Users, Package, PercentCircle } from 'lucide-react';

// ===============================
// Dados de exemplo (podem ser substituídos por dados reais)
// ===============================

// Dados para o LineChart de Vendas Mensais
const monthlySalesData = [
    { month: 'Jan', vendas: 2500, receita: 30000 },
    { month: 'Fev', vendas: 3200, receita: 38000 },
    { month: 'Mar', vendas: 2800, receita: 35000 },
    { month: 'Abr', vendas: 4000, receita: 48000 },
    { month: 'Mai', vendas: 4500, receita: 55000 },
    { month: 'Jun', vendas: 5000, receita: 62000 },
    { month: 'Jul', vendas: 5500, receita: 68000 },
    { month: 'Ago', vendas: 6000, receita: 75000 },
    { month: 'Set', vendas: 5800, receita: 72000 },
    { month: 'Out', vendas: 6500, receita: 80000 },
    { month: 'Nov', vendas: 7000, receita: 88000 },
    { month: 'Dez', vendas: 7500, receita: 95000 },
];

// Dados para o BarChart de Desempenho de Produtos
const productPerformanceData = [
    { produto: 'Produto A', vendas: 120, lucro: 2500, cor: '#0088FE' },
    { produto: 'Produto B', vendas: 85, lucro: 1800, cor: '#00C49F' },
    { produto: 'Produto C', vendas: 150, lucro: 3000, cor: '#FFBB28' },
    { produto: 'Produto D', vendas: 95, lucro: 2000, cor: '#FF8042' },
    { produto: 'Produto E', vendas: 200, lucro: 4000, cor: '#8884d8' },
];

// Dados para o PieChart de Categorias de Produtos
const productCategoryData = [
    { id: 'Eletrônicos', label: 'Eletrônicos', value: 300, cor: '#FF6B6B' },
    { id: 'Vestuário', label: 'Vestuário', value: 250, cor: '#46BFBD' },
    { id: 'Alimentos', label: 'Alimentos', value: 180, cor: '#FDB45C' },
    { id: 'Casa e Decoração', label: 'Casa e Decoração', value: 220, cor: '#949F7E' },
    { id: 'Livros', label: 'Livros', value: 100, cor: '#4D5360' },
];

// Dados para o Funil de Conversão de Vendas
const salesFunnelData = [
    { Etapa: 'Visitas', Valor: 1200 },
    { Etapa: 'Carrinho', Valor: 800 },
    { Etapa: 'Checkout', Valor: 500 },
    { Etapa: 'Compra', Valor: 300 },
];

// Dados para o Gráfico de Radar de Desempenho de Vendas por Região
const salesPerformanceByRegionData = [
    {
        regiao: 'Norte',
        vendas: 80,
        lucro: 60,
        clientes: 100,
    },
    {
        regiao: 'Nordeste',
        vendas: 60,
        lucro: 45,
        clientes: 80,
    },
    {
        regiao: 'Centro-Oeste',
        vendas: 70,
        lucro: 55,
        clientes: 90,
    },
    {
        regiao: 'Sudeste',
        vendas: 100,
        lucro: 80,
        clientes: 120,
    },
    {
        regiao: 'Sul',
        vendas: 90,
        lucro: 70,
        clientes: 110,
    },
];

// Dados para o Gráfico de Bolhas de Análise de Clientes
const customerAnalysisData = {
    nodes: [
        { id: 'Cliente A', valor: 50, x: 20, y: 30, categoria: 'Premium', cor: '#F44336' },
        { id: 'Cliente B', valor: 30, x: 50, y: 70, categoria: 'Regular', cor: '#2196F3' },
        { id: 'Cliente C', valor: 80, x: 80, y: 20, categoria: 'Vip', cor: '#4CAF50' },
        { id: 'Cliente D', valor: 20, x: 10, y: 60, categoria: 'Regular', cor: '#2196F3' },
        { id: 'Cliente E', valor: 60, x: 60, y: 40, categoria: 'Premium', cor: '#F44336' },
        { id: 'Cliente F', valor: 40, x: 90, y: 80, categoria: 'Regular', cor: '#2196F3' },
        { id: 'Cliente G', valor: 90, x: 30, y: 10, categoria: 'Vip', cor: '#4CAF50' },
        { id: 'Cliente H', valor: 10, x: 70, y: 50, categoria: 'Basico', cor: '#9E9E9E' },
        { id: 'Cliente I', valor: 70, x: 40, y: 90, categoria: 'Premium', cor: '#F44336' },
        { id: 'Cliente J', valor: 55, x: 100, y: 65, categoria: 'Vip', cor: '#4CAF50' },
    ],
};

// Cores para o PieChart
const pieChartColors = ['#FF6B6B', '#46BFBD', '#FDB45C', '#949F7E', '#4D5360'];

// Cores para o Funil
const funnelColors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const Dashboard1 = () => {
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 30)), // 30 dias atrás
        endDate: new Date(), // Data atual
    });

    const handleDateChange = (range) => {
        setDateRange({
            startDate: range.from,
            endDate: range.to,
        });
    };

    // Formata a data para exibição
    const formatDate = (date) => {
        return date.toLocaleDateString('pt-BR');
    };

    return (
        <div className="p-6 bg-gray-950 min-h-screen">
            <h1 className="text-3xl font-bold text-white mb-8 text-center bg-gradient-to-r from-purple-500 to-blue-500 text-transparent bg-clip-text">
                Painel de Controle
            </h1>

            {/* Seletor de Data */}
            <div className="flex justify-center mb-8">
                <div className="flex items-center gap-4">
                    <span className="text-gray-300">Período:</span>
                    <Button
                        variant="outline"
                        className="bg-gray-800 hover:bg-gray-700 text-gray-200 border-gray-700"
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formatDate(dateRange.startDate)} - {formatDate(dateRange.endDate)}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* Cartão de Vendas Totais */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card
                        title={
                            <div className="text-xl font-semibold text-white flex items-center gap-2">
                                <ShoppingCart className="h-5 w-5 text-blue-400" />
                                Vendas Totais
                            </div>
                        }
                        className="bg-gray-900 border-gray-800 shadow-lg"
                    >
                        <p className="text-2xl font-bold text-green-400">R$ 2,500,000</p>
                        <p className="text-sm text-gray-400">
                            <TrendingUp className="inline-block h-4 w-4 mr-1" />
                            +20% em relação ao mês passado
                        </p>
                    </Card>
                </motion.div>

                {/* Cartão de Receita Total */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <Card
                        title={
                            <div className="text-xl font-semibold text-white flex items-center gap-2">
                                <Banknote className="h-5 w-5 text-green-400" />
                                Receita Total
                            </div>
                        }
                        className="bg-gray-900 border-gray-800 shadow-lg"
                    >
                        <p className="text-2xl font-bold text-yellow-400">R$ 3,200,000</p>
                        <p className="text-sm text-gray-400">
                            <TrendingUp className="inline-block h-4 w-4 mr-1" />
                            +15% em relação ao mês passado
                        </p>
                    </Card>
                </motion.div>

                {/* Cartão de Clientes Ativos */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <Card
                        title={
                            <div className="text-xl font-semibold text-white flex items-center gap-2">
                                <Users className="h-5 w-5 text-purple-400" />
                                Clientes Ativos
                            </div>
                        }
                        className="bg-gray-900 border-gray-800 shadow-lg"
                    >
                        <p className="text-2xl font-bold text-pink-400">12,500</p>
                        <p className="text-sm text-gray-400">
                            <TrendingUp className="inline-block h-4 w-4 mr-1" />
                            +10% em relação ao mês passado
                        </p>
                    </Card>
                </motion.div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                {/* Gráfico de Linhas de Vendas Mensais */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card
                        title="Vendas Mensais"
                        className="bg-gray-900 border-gray-800 shadow-lg"
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={monthlySalesData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                                <XAxis dataKey="month" stroke="#fff" />
                                <YAxis stroke="#fff" tickFormatter={(value) => `R$${value}`} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#222', borderColor: '#444', color: '#fff' }}
                                    labelStyle={{ color: '#fff' }}
                                    itemStyle={{ color: '#fff' }}
                                    formatter={(value) => `R$${value}`}
                                />
                                <Legend wrapperStyle={{ color: '#fff' }} />
                                <Line type="monotone" dataKey="vendas" stroke="#8884d8" activeDot={{ r: 8 }} name="Vendas" />
                                <Line type="monotone" dataKey="receita" stroke="#82ca9d" activeDot={{ r: 8 }} name="Receita" />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                </motion.div>

                {/* Gráfico de Barras de Desempenho de Produtos */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <Card
                        title="Desempenho de Produtos"
                        className="bg-gray-900 border-gray-800 shadow-lg"
                    >
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={productPerformanceData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                                <XAxis dataKey="produto" stroke="#fff" />
                                <YAxis stroke="#fff" tickFormatter={(value) => `R$${value}`} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#222', borderColor: '#444', color: '#fff' }}
                                    labelStyle={{ color: '#fff' }}
                                    itemStyle={{ color: '#fff' }}
                                    formatter={(value) => `R$${value}`}
                                />
                                <Legend wrapperStyle={{ color: '#fff' }} />
                                <Bar dataKey="vendas" fill="#8884d8" name="Vendas" />
                                <Bar dataKey="lucro" fill="#82ca9d" name="Lucro" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </motion.div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                {/* Gráfico de Pizza de Categorias de Produtos */}
                <motion.div
                    initial={{ opacity: 0, rotate: -10 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card
                        title="Categorias de Produtos"
                        className="bg-gray-900 border-gray-800 shadow-lg"
                    >
                        <div className="flex items-center justify-center">
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart width={400} height={300}>
                                    <Pie
                                        data={productCategoryData}
                                        dataKey="value"
                                        nameKey="label"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        fill="#8884d8"
                                    >
                                        {productCategoryData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={pieChartColors[index % pieChartColors.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </motion.div>

                {/* Funil de Conversão de Vendas */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <Card
                        title="Funil de Conversão de Vendas"
                        className="bg-gray-900 border-gray-800 shadow-lg"
                    >
                        <div className="flex items-center justify-center">
                            <ResponsiveContainer width="100%" height={300}>
                                {/* Funnel chart is not available in recharts */}
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </motion.div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                {/* Gráfico de Radar de Desempenho de Vendas por Região */}
                <motion.div
                    initial={{ opacity: 0, rotate: 10 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card
                        title="Desempenho de Vendas por Região"
                        className="bg-gray-900 border-gray-800 shadow-lg"
                    >
                        <div className="flex items-center justify-center">
                            <ResponsiveContainer width="100%" height={300}>
                                <RadarChart outerRadius={90} width={400} height={300} data={salesPerformanceByRegionData}>
                                    <PolarGrid />
                                    <PolarAngleAxis dataKey="regiao" />
                                    <PolarRadiusAxis />
                                    <Radar name="Vendas" dataKey="vendas" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                                    <Radar name="Lucro" dataKey="lucro" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                                    <Radar name="Clientes" dataKey="clientes" stroke="#ffc658" fill="#ffc658" fillOpacity={0.6} />
                                    <Legend />
                                    <Tooltip />
                                </RadarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </motion.div>

                {/* Gráfico de Bolhas de Análise de Clientes */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <Card
                        title="Análise de Clientes"
                        className="bg-gray-900 border-gray-800 shadow-lg"
                    >
                        <div className="flex items-center justify-center">
                            <ResponsiveContainer width="100%" height={300}>
                                {/* Bubble chart is not available in recharts */}
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </motion.div>
            </div>
        </div>
    );
};

export default Dashboard1;

